import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';
import PageGuide from '../components/PageGuide';
import { ClipButton, downloadBlob } from '../components/ReportWorkspace';
import TerritoryView from '../components/TerritoryView';
import { qualityPercent } from '../utils/evidenceStatus';
import '../styles/FieldTeamCards.css';
import '../styles/NewDashboard.css';

import { typeLabel } from '../utils/entityLabels';
import filterParams from '../utils/filterParams';

const teamCache = new Map();
const CACHE_MS = 5 * 60 * 1000;

function FieldTeamV2({ filters, loading, setLoading, currentUser }) {
  const [stats, setStats] = useState([]);
  const [activeTab, setActiveTab] = useState('contribution');
  const [statsLoading, setStatsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [performance, setPerformance] = useState([]);
  const [insights, setInsights] = useState({ regions: [], agents: [] });
  const [assignmentMessage, setAssignmentMessage] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [agentDetail, setAgentDetail] = useState({}); // { [agent_id]: { entities, recordings, loading } }
  const isAdmin = ['admin', 'admin_dachido'].includes(String(currentUser?.role || '').toLowerCase());

  // Use the same selected period as the rest of the dashboard.
  const buildParams = useCallback(() => filterParams(filters), [filters]);

  useEffect(() => {
    const params = buildParams();
    const key = params.toString();
    const cached = teamCache.get(key);
    if (cached && Date.now() - cached.at < CACHE_MS && !refreshKey) {
      setStats(cached.stats);
      setPerformance(cached.performance);
      setInsights(cached.insights);
      setLoadError('');
      setStatsLoading(false);
      setLoading(false);
      return;
    }
    setStatsLoading(true);
    setLoadError('');
    let active = true;
    Promise.all([
      axios.get(`/api/agent-view/stats?${params}`),
      axios.get('/api/agent-view/performance').catch(() => ({ data: { data: [] } })),
      axios.get(`/api/agent-view/insights?${params}`).catch(() => ({ data: { regions: [], agents: [] } })),
    ])
      .then(([statsRes, performanceRes, insightsRes]) => {
        if (!active) return;
        const result = {
          stats: statsRes.data?.data || [],
          performance: performanceRes.data?.data || [],
          insights: insightsRes.data || { regions: [], agents: [] },
          at: Date.now(),
        };
        teamCache.set(key, result);
        setStats(result.stats);
        setPerformance(result.performance);
        setInsights(result.insights);
      })
      .catch(() => {
        if (active) setLoadError('Field Team data did not load. Select Refresh data to try again.');
      })
      .finally(() => {
        if (active) {
          setStatsLoading(false);
          setLoading(false);
        }
      });
    // Reset any open expansion when filters change — the cached per-agent data is
    // tied to the old filter window.
    setExpandedAgentId(null);
    setAgentDetail({});
    return () => {
      active = false;
    };
  }, [filters, buildParams, setLoading, refreshKey]);

  const loadAgentDetail = useCallback(
    (agentId) => {
      setAgentDetail((prev) => ({ ...prev, [agentId]: { ...(prev[agentId] || {}), loading: true } }));
      const params = buildParams();
      params.append('agent_id', agentId);
      const url = (path) => `/api/agent-view/${path}?${params}`;
      Promise.all([
        axios
          .get(url('agent-entities'))
          .then((r) => r.data || {})
          .catch(() => ({})),
        axios
          .get(url('agent-recordings'))
          .then((r) => r.data?.data || [])
          .catch(() => []),
      ]).then(([entitiesRes, recordings]) => {
        setAgentDetail((prev) => ({
          ...prev,
          [agentId]: {
            entities: entitiesRes.data || [],
            availableTypes: entitiesRes.available_types || [],
            entityType: '',
            recordings,
            loading: false,
          },
        }));
      });
    },
    [buildParams],
  );

  // The type filter is a SQL predicate (the histogram is capped at 30 rows, so filtering
  // client-side over an already-truncated list would hide minority types), which means a
  // type change needs a request. Only the histogram is refetched — recordings per day do
  // not depend on entity type.
  const changeAgentEntityType = useCallback(
    (agentId, entityType) => {
      setAgentDetail((prev) => ({
        ...prev,
        [agentId]: { ...(prev[agentId] || {}), entityType, entitiesLoading: true },
      }));
      const params = buildParams();
      params.append('agent_id', agentId);
      if (entityType) params.append('entity_type', entityType);
      axios
        .get(`/api/agent-view/agent-entities?${params}`)
        .then((r) =>
          setAgentDetail((prev) => ({
            ...prev,
            [agentId]: {
              ...(prev[agentId] || {}),
              entities: r.data?.data || [],
              // A narrowed request still returns every type, but keep the previous list on a
              // partial response so the picker cannot lose its options mid-session.
              availableTypes: r.data?.available_types || prev[agentId]?.availableTypes || [],
              entitiesLoading: false,
            },
          })),
        )
        .catch(() =>
          setAgentDetail((prev) => ({
            ...prev,
            [agentId]: { ...(prev[agentId] || {}), entitiesLoading: false },
          })),
        );
    },
    [buildParams],
  );

  const toggleAgent = (agentId) => {
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null);
      return;
    }
    setExpandedAgentId(agentId);
    if (!agentDetail[agentId]) {
      loadAgentDetail(agentId);
    }
  };

  const performanceByAgent = Object.fromEntries(performance.map((row) => [row.agent_id, row]));
  const reviewed = performance.reduce((sum, row) => sum + Number(row.conversations_reviewed || 0), 0);
  const avgScore = performance.length
    ? Math.round(
        performance.reduce((sum, row) => sum + qualityPercent(row.overall_agent_score), 0) /
          performance.length,
      )
    : null;
  const maxRegionTopics = Math.max(...insights.regions.map((x) => Number(x.distinct_topics || 0)), 1);
  const regionAgentIds = new Set(
    insights.agents
      .filter((x) => !selectedRegion || x.region === selectedRegion)
      .map((x) => String(x.agent_id)),
  );
  const comparisonRows = stats
    .filter((row) => !selectedRegion || regionAgentIds.has(String(row.agent_id)))
    .map((row) => ({ ...row, ...(performanceByAgent[row.agent_id] || {}) }));
  const nationalTopicsPerConversation =
    insights.agents.reduce((sum, x) => sum + Number(x.distinct_topics || 0), 0) /
    Math.max(
      1,
      insights.agents.reduce((sum, x) => sum + Number(x.conversations || 0), 0),
    );
  const regionTopicRows = insights.agents.filter((x) => !selectedRegion || x.region === selectedRegion);
  const regionTopicsPerConversation =
    regionTopicRows.reduce((sum, x) => sum + Number(x.distinct_topics || 0), 0) /
    Math.max(
      1,
      regionTopicRows.reduce((sum, x) => sum + Number(x.conversations || 0), 0),
    );
  const reportBase = {
    page: 'Field Team',
    subtitle: 'Recorded field-team contribution',
    finding: 'Compare conversation coverage before interpreting differences in topic breadth.',
  };

  const downloadAssignments = async () => {
    try {
      const response = await axios.get('/api/agent-view/assignments/template', { responseType: 'blob' });
      downloadBlob(response.data, 'FieldForce_Agent_Assignments.xlsx');
    } catch {
      setAssignmentMessage('Could not download the assignment template.');
    }
  };
  const uploadAssignments = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await axios.post('/api/agent-view/assignments/upload', form);
      setAssignmentMessage(
        `${response.data.rows_loaded} assignments saved. Product-team comparisons are refreshed.`,
      );
      setRefreshKey((x) => x + 1);
    } catch (error) {
      setAssignmentMessage(
        error.response?.data?.details?.join('; ') || error.response?.data?.error || 'Upload failed.',
      );
    }
    event.target.value = '';
  };

  return (
    <div className="v2-page">
      <div className="module-header">
        <div className="module-kicker">FIELD TEAM</div>
        <h2>Field Team</h2>
        <p>
          Who is contributing useful market coverage, where coverage is thin, and what each person can
          improve.
        </p>
        <button onClick={() => setRefreshKey((x) => x + 1)}>Refresh data</button>
      </div>
      <FilterBar />
      <div className="team-view-tabs" role="tablist" aria-label="Field Team views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'contribution'}
          className={activeTab === 'contribution' ? 'active' : ''}
          onClick={() => setActiveTab('contribution')}
        >
          Contribution
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'territory'}
          className={activeTab === 'territory' ? 'active' : ''}
          onClick={() => setActiveTab('territory')}
        >
          Territory
        </button>
      </div>
      {activeTab === 'contribution' ? (
        <>
          {loadError && (
            <div className="decision-empty" role="alert">
              {loadError}
            </div>
          )}

          <PageGuide
            purpose="Compare contribution and coverage fairly, then open an individual row to review what they captured."
            steps={[
              'Check conversation coverage before comparing people.',
              'Compare topics found per conversation as an insight-depth indicator.',
              'Open a person to review their topics and recording consistency.',
            ]}
            metrics={[
              {
                name: 'Conversations',
                definition: 'Distinct conversations attributed to the field team member.',
              },
              {
                name: 'Topics per conversation',
                definition:
                  'Average approved brands, crops, issues and other topics identified per conversation. It is not a quality score on its own.',
              },
              {
                name: 'Average words',
                definition:
                  'Average transcript word count, used only as a rough indicator of conversation depth.',
              },
              {
                name: 'Audio minutes',
                definition: 'Average and total recorded duration in the selected period.',
              },
            ]}
          />
          {isAdmin && (
            <div className="team-assignment-tools">
              <b>Team assignments</b>
              <span>Use verified onboarding data for assigned province and product team comparisons.</span>
              <button onClick={downloadAssignments}>Download Excel template</button>
              <label className="team-upload-label">
                Upload completed template
                <input type="file" accept=".xlsx" onChange={uploadAssignments} />
              </label>
              {assignmentMessage && <small role="status">{assignmentMessage}</small>}
            </div>
          )}
          {statsLoading ? (
            <div className="decision-empty" role="status">
              Loading Field Team data…
            </div>
          ) : loadError ? null : (
            <>
              <div
                style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}
              >
                <label>
                  Conversation location{' '}
                  <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
                    <option value="">All locations</option>
                    {insights.regions.map((x) => (
                      <option key={x.region} value={x.region}>
                        {x.region}
                      </option>
                    ))}
                  </select>
                </label>
                <span>
                  Topics per conversation:{' '}
                  {selectedRegion ? `${regionTopicsPerConversation.toFixed(1)} here · ` : ''}
                  {nationalTopicsPerConversation.toFixed(1)} nationally
                </span>
                <small>Location is where conversations were recorded, not an assigned territory.</small>
              </div>

              <div className="team-kpis">
                <div className="card">
                  <small>Contributing team members</small>
                  <strong>{stats.length}</strong>
                  <span>with recorded conversations</span>
                </div>
                <div className="card">
                  <small>Conversations captured</small>
                  <strong>{stats.reduce((s, x) => s + Number(x.conversation_count || 0), 0)}</strong>
                  <span>in the selected period</span>
                </div>
                <div className="card">
                  <small>Conversation quality review</small>
                  <strong>{avgScore == null ? '—' : `${avgScore}%`}</strong>
                  <span>{reviewed} conversations reviewed</span>
                </div>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <Panel
                  title="Insight contribution by region"
                  subtitle="Distinct approved topics provide breadth; issue topics show field-problem coverage"
                  actions={
                    <ClipButton
                      item={{
                        ...reportBase,
                        title: 'Insight contribution by region',
                        visual: 'bars',
                        rows: insights.regions.slice(0, 10).map((x) => ({
                          label: x.region,
                          value: `${x.distinct_topics} topics`,
                          status: 'none',
                        })),
                      }}
                    />
                  }
                >
                  <div className="team-region-bars">
                    {insights.regions.slice(0, 10).map((x) => (
                      <div key={x.region}>
                        <span>
                          <b>{x.region}</b>
                          <small>
                            {x.conversations} conversations · {x.contributors} contributors · {x.issue_topics}{' '}
                            issue topics
                          </small>
                        </span>
                        <i>
                          <b
                            style={{
                              width: `${Math.max(4, (Number(x.distinct_topics) * 100) / maxRegionTopics)}%`,
                            }}
                          />
                        </i>
                        <strong>{x.distinct_topics}</strong>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>

              <Panel
                title="Field team contribution"
                subtitle="Select a person to see the topics they captured and their recording pattern"
                loading={statsLoading}
                actions={
                  <ClipButton
                    item={{
                      ...reportBase,
                      title: 'Field team contribution',
                      visual: 'table',
                      rows: comparisonRows.slice(0, 10).map((x) => ({
                        label: x.agent_name,
                        value: `${x.conversation_count} conversations`,
                        status: 'none',
                      })),
                    }}
                  />
                }
              >
                {stats.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                          <th style={thStyle}></th>
                          <th style={thStyle}>Field team member</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Conversations</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Topics / conversation</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Quality review</th>
                          <th style={thStyle}>Coaching focus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonRows.map((row) => {
                          const isOpen = expandedAgentId === row.agent_id;
                          const detail = agentDetail[row.agent_id];
                          const quality = performanceByAgent[row.agent_id];
                          return (
                            <React.Fragment key={row.agent_id}>
                              <tr
                                onClick={() => toggleAgent(row.agent_id)}
                                style={{
                                  borderBottom: '1px solid var(--card-border)',
                                  cursor: 'pointer',
                                  background: isOpen
                                    ? 'var(--card-bg-hover, rgba(31,94,255,0.04))'
                                    : 'transparent',
                                }}
                              >
                                <td style={{ ...tdStyle, width: '24px', color: 'var(--text-lighter)' }}>
                                  {isOpen ? '▾' : '▸'}
                                </td>
                                <td style={tdStyle}>{row.agent_name}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{row.conversation_count}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                  {row.entities_per_conversation ?? '—'}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                  {quality?.overall_agent_score == null
                                    ? '—'
                                    : `${qualityPercent(quality.overall_agent_score)}%`}
                                </td>
                                <td style={{ ...tdStyle, maxWidth: '220px' }}>
                                  {quality?.coaching_priority || quality?.primary_gap || 'Not yet reviewed'}
                                </td>
                              </tr>
                              {isOpen && (
                                <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                  <td
                                    colSpan={6}
                                    style={{
                                      padding: '16px 14px 24px',
                                      background: 'var(--card-bg-hover, rgba(0,0,0,0.015))',
                                    }}
                                  >
                                    <AgentDetail
                                      detail={detail}
                                      agentName={row.agent_name}
                                      filters={filters}
                                      quality={quality}
                                      onEntityTypeChange={(t) => changeAgentEntityType(row.agent_id, t)}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : !statsLoading && !loadError ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '24px',
                      color: 'var(--text-lighter)',
                      fontSize: '14px',
                    }}
                  >
                    No agent data available for the selected filters.
                  </div>
                ) : null}
              </Panel>
            </>
          )}
        </>
      ) : (
        <TerritoryView filters={filters} refreshKey={refreshKey} />
      )}
    </div>
  );
}

function toISODate(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function recordingsRange(filters) {
  const today = new Date();
  const toISO = filters.date_to || today.toISOString().slice(0, 10);
  const fromISO = filters.date_from || new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  return [fromISO, toISO];
}

function AgentDetail({ detail, agentName, filters, quality, onEntityTypeChange }) {
  if (!detail || detail.loading) {
    return <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>;
  }
  const {
    entities = [],
    recordings = [],
    availableTypes = [],
    entityType = '',
    entitiesLoading = false,
  } = detail;
  const chartLayout = {
    margin: { t: 30, r: 20, b: 80, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { family: 'Inter, sans-serif', color: 'var(--text-dark)' },
  };
  const chartConfig = { displayModeBar: false, responsive: true };
  const [xMin, xMax] = recordingsRange(filters);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {quality && (
        <div
          className="card"
          style={{
            padding: '14px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
            gap: '12px',
          }}
        >
          <div>
            <div style={miniTitleStyle}>Demonstrated strength</div>
            <b>{quality.primary_strength || 'Not assessed'}</b>
          </div>
          <div>
            <div style={miniTitleStyle}>Development area</div>
            <b>{quality.primary_gap || 'Not assessed'}</b>
          </div>
          <div>
            <div style={miniTitleStyle}>Suggested coaching focus</div>
            <b>{quality.coaching_priority || 'Not assessed'}</b>
          </div>
        </div>
      )}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '8px',
          }}
        >
          <div style={{ ...miniTitleStyle, marginBottom: 0 }}>Entities mentioned by {agentName}</div>
          {availableTypes.length > 0 && (
            <select
              aria-label="Entity type"
              value={entityType}
              onChange={(e) => onEntityTypeChange(e.target.value)}
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'inherit',
                border: '1px solid var(--card-border)',
                borderRadius: '8px',
                background: 'var(--card-bg)',
                color: 'var(--text-dark)',
              }}
            >
              <option value="">All types</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </select>
          )}
        </div>
        {entitiesLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>
        ) : entities.length > 0 ? (
          <Plot
            data={[
              {
                x: entities.map((d) => d.entity_name),
                y: entities.map((d) => Number(d.mention_count)),
                type: 'bar',
                marker: { color: '#1f5eff' },
              },
            ]}
            layout={{
              ...chartLayout,
              xaxis: { title: 'Entity', tickangle: -45, automargin: true },
              yaxis: { title: 'Mention Count' },
            }}
            config={chartConfig}
            useResizeHandler
            style={{ width: '100%', height: '360px' }}
          />
        ) : (
          <div style={emptyStyle}>No entity mentions in this time range.</div>
        )}
      </div>

      <div>
        <div style={miniTitleStyle}>Recordings per day</div>
        {recordings.length > 0 ? (
          <Plot
            data={[
              {
                x: recordings.map((d) => toISODate(d.day)),
                y: recordings.map((d) => Number(d.recordings)),
                type: 'bar',
                marker: { color: '#c98b12' },
              },
            ]}
            layout={{
              ...chartLayout,
              xaxis: {
                title: 'Date',
                type: 'date',
                range: [xMin, xMax],
                tickformat: '%b %d',
                automargin: true,
              },
              yaxis: { title: 'Recordings', rangemode: 'tozero', tickformat: 'd' },
            }}
            config={chartConfig}
            useResizeHandler
            style={{ width: '100%', height: '320px' }}
          />
        ) : (
          <div style={emptyStyle}>No recordings in this time range.</div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: '10px',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: 'var(--text-lighter)',
};
const tdStyle = {
  padding: '12px 14px',
  color: 'var(--text-dark)',
};
const miniTitleStyle = {
  fontSize: '11px',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: 'var(--text-lighter)',
  marginBottom: '8px',
};
const emptyStyle = {
  textAlign: 'center',
  padding: '24px',
  color: 'var(--text-lighter)',
  fontSize: '14px',
};

export default FieldTeamV2;
